import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PageHeader from '../../../shared/page-header/PageHeader';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { getModulePermissions } from '../../../utils/permissionHelper';
import { logAuditEvent } from '../../../utils/auditLogger';
import { getStorageValues } from '../../../shared/CommonFunctions';
import { exportToExcel, exportToPDF } from '../../../utils/downloadExportedFiles';
import ProductSubCategoryGrid from './ProductSubCategoryGrid';
import ProductSubCategoryModal from './ProductSubCategoryModal';
import { buildProductSubCategorySavePayload } from './utils/mapProductSubCategory';
import { validateProductSubCategory } from './productSubCategoryValidation';

function ProductSubCategoryMain(props) {
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [userAction, setUserAction] = useState('Add');
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [alert, setAlert] = useState(null);
  const [userModulePermission, setUserModulePermission] = useState({});
  const moduleName = props.Module || 'Product Sub Category';

  const fetchSubCategories = async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/ec-product-sub-category/getAll`);
      if (response.status === 200) {
        setSubCategoryList(response.data?.data || []);
      }
    } catch (error) {
      console.error(error);
      CreateAlert('Failed to load product sub categories!!', 'Error', setAlert);
    }
  };

  useEffect(() => {
    const modulePermissions = getModulePermissions(props.Module);
    setUserModulePermission(modulePermissions ? modulePermissions : []);
    fetchSubCategories();
    logAuditEvent(moduleName, 'Accessed Module', 'Accessed Product Sub Category module');
  }, [props.Module, moduleName]);

  const openAddModal = () => {
    setUserAction('Add');
    setSelectedSubCategory(null);
    setIsOpen(true);
    logAuditEvent(moduleName, 'Add Button Click', 'Opened add product sub category modal');
  };

  const closeModal = () => {
    setIsOpen(false);
    setSelectedSubCategory(null);
    setUserAction('Add');
  };

  const onSubmit = async (e, data) => {
    if (e.target.name !== 'Add' && e.target.name !== 'Edit') return;

    const payload = buildProductSubCategorySavePayload(data, getStorageValues('userId'));
    if (!validateProductSubCategory(payload, setAlert)) return;

    try {
      const response = await axios.post(`${getApiBaseUrl()}/ec-product-sub-category/save`, payload);
      const result = response.data?.data;
      if (result?.success === 1) {
        const isAdd = e.target.name === 'Add';
        CreateAlert(
          result.message || (isAdd ? 'Sub category added successfully!!' : 'Sub category updated successfully!!'),
          'Success',
          setAlert
        );
        await fetchSubCategories();
        if (isAdd) {
          closeModal();
        }
      } else {
        CreateAlert(result?.message || 'Failed to save sub category!!', 'Error', setAlert);
      }
    } catch (error) {
      console.error(error);
      CreateAlert('Failed to save sub category!!', 'Error', setAlert);
    }
  };

  const handleExportExcel = () => {
    const columns = [
      { name: 'Category', selector: 'CategoryName' },
      { name: 'Sub Category', selector: 'SubCategoryName' },
      { name: 'Slug', selector: 'Slug' },
      { name: 'Image URL', selector: 'ImageUrl' },
      { name: 'Display Order', selector: 'DisplayOrder' },
      { name: 'Active', selector: 'IsActive' },
    ];
    exportToExcel('Product Sub Categories', columns, subCategoryList);
  };

  const handleExportPDF = () => {
    const columns = [
      { name: 'Category', selector: 'CategoryName' },
      { name: 'Sub Category', selector: 'SubCategoryName' },
      { name: 'Slug', selector: 'Slug' },
      { name: 'Image URL', selector: 'ImageUrl' },
      { name: 'Display Order', selector: 'DisplayOrder' },
      { name: 'Active', selector: 'IsActive' },
    ];
    exportToPDF('Product Sub Categories', columns, subCategoryList);
  };

  return (
    <>
      <SKAlerts alert={alert} />
      <PageHeader
        displayModule={props.Module}
        onModelOpen={openAddModal}
        onRefresh={fetchSubCategories}
        showPDF={true}
        showExcel={true}
        showPrint={false}
        showRefresh={true}
        onExcelExport={handleExportExcel}
        onPdfExport={handleExportPDF}
        showAddButton={userModulePermission.AddModule === 1}
      />
      <ProductSubCategoryGrid
        SubCategoryData={subCategoryList}
        fetchSubCategories={fetchSubCategories}
        UserPermissions={userModulePermission}
        moduleName={moduleName}
      />
      <ProductSubCategoryModal
        show={modalIsOpen}
        UserAction={userAction}
        selectedRow={selectedSubCategory}
        onClose={closeModal}
        onSubmit={onSubmit}
        moduleName={moduleName}
      />
    </>
  );
}

export default ProductSubCategoryMain;
